import { SubscribePushDto } from "@myapp/contract";
import { isPushSupported } from "./pushSupport";
import { urlBase64ToUint8Array } from "./urlBase64";

/**
 * The browser half of a push subscription. No network calls and no React — posting the result
 * to the API is the entity layer's job.
 */

/** The subscription this browser already holds, if any. Null is normal, not an error. */
export async function getLocalSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

function toDto(subscription: PushSubscription): SubscribePushDto {
  // toJSON() is the only supported way to read the keys; the fields are not on the instance.
  const json = subscription.toJSON();
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: json.keys?.p256dh ?? "",
      auth: json.keys?.auth ?? "",
    },
    userAgent: navigator.userAgent.slice(0, 512),
  };
}

/**
 * Reuse the existing subscription or create one.
 *
 * `userVisibleOnly: true` is not optional in Chrome — without it `subscribe()` rejects with a
 * confusing `NotSupportedError` — and it is also the promise the `push` handler keeps by
 * always calling `showNotification`.
 */
export async function createLocalSubscription(vapidPublicKey: string): Promise<SubscribePushDto> {
  const registration = await navigator.serviceWorker.ready;

  const existing = await registration.pushManager.getSubscription();
  if (existing) return toDto(existing);

  const created = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });
  return toDto(created);
}

/**
 * Drop this browser's subscription and report the endpoint that was removed, so the caller can
 * tell the server which row to delete. Returns null when there was nothing to drop.
 */
export async function dropLocalSubscription(): Promise<string | null> {
  const subscription = await getLocalSubscription();
  if (!subscription) return null;

  const { endpoint } = subscription;
  await subscription.unsubscribe();
  return endpoint;
}
